import * as React from "react";
import { useTranslation } from "react-i18next";
import { useState, useCallback } from "react";
import styled, { css } from "styled-components";
import { useQueryClient } from "@tanstack/react-query";
import { COLLECTIONS_QUERY_KEY } from "collections/hooks/useCollections";
import { BootstrapModalConfirm, Col, MenuItem, DeleteMenuItem } from "components/bootstrap";
import { useGetLinkedProceduresForStep, useDeleteEventProcedureStep } from "security-app/hooks/useEventProceduresAPI";
import { ConfirmDialog, Icon, IfPermitted, HasOwnership } from "components/common";
import { useModalContext } from "common/contexts";
import EntityShareModal from "components/permissions/EntityShareModal";
import usePluggableEntitySharedActions from "hooks/usePluggableEntitySharedActions";
import useGetPermissionsByScope from "hooks/useScopePermissions";
import { MoreActionsMenu } from "components/common/MoreActions";
const Wrapper = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: ${theme.spacings.xs};
  `
);
const StyledCol = styled(Col)`
  display: flex;
  flex-direction: column;
  padding: 0.6rem;
  gap: 0.3rem;
`;
function RowActionMenu({ eventProcedureStep }) {
  const { t } = useTranslation("enterprise_security_events");
  const { setModal, setEntity } = useModalContext();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteWarningModal, setShowDeleteWarningModal] = useState(false);
  const [showEntityShareModal, setShowEntityShareModal] = useState(false);
  const queryClient = useQueryClient();
  const { linkedEventProcedures, loadingLinkedEventProcedures } = useGetLinkedProceduresForStep(eventProcedureStep.id);
  const { deleteEventProcedureStep } = useDeleteEventProcedureStep();
  const { scopePermissions } = useGetPermissionsByScope(eventProcedureStep);
  const isMutable = !!(scopePermissions == null ? void 0 : scopePermissions.is_mutable);
  const { actions: pluggableActions, actionModals: pluggableActionModals } = usePluggableEntitySharedActions(
    eventProcedureStep,
    "event_procedure_step",
    () => queryClient.invalidateQueries({ queryKey: [COLLECTIONS_QUERY_KEY] })
  );
  const moreActions = [pluggableActions.length ? pluggableActions : null].filter(Boolean);
  const onEdit = () => {
    setEntity(eventProcedureStep);
    setModal("EDIT-EVENT-PROCEDURE-STEP");
  };
  const onDelete = async () => {
    await deleteEventProcedureStep({ id: eventProcedureStep.id });
    setShowDeleteModal(false);
  };
  const handleDelete = () => {
    if (loadingLinkedEventProcedures) return;
    if (linkedEventProcedures.length < 1) {
      setShowDeleteModal(true);
    } else {
      setShowDeleteWarningModal(true);
    }
  };
  const toggleEntityShareModal = useCallback(() => {
    setShowEntityShareModal((cur) => !cur);
  }, []);
  return /* @__PURE__ */ React.createElement(Wrapper, null, /* @__PURE__ */ React.createElement(MoreActionsMenu, { title: t("step_row_actions.toggle_actions") }, isMutable && /* @__PURE__ */ React.createElement(IfPermitted, { permissions: [`event_procedure_steps:edit:${eventProcedureStep.id}`] }, /* @__PURE__ */ React.createElement(MenuItem, { onClick: onEdit }, /* @__PURE__ */ React.createElement(Icon, { name: "edit_square", size: "xs" }), " ", t("step_row_actions.edit"))), /* @__PURE__ */ React.createElement(HasOwnership, { id: eventProcedureStep.id, type: "event_procedure_step" }, ({ disabled: hasMissingPermissions }) => /* @__PURE__ */ React.createElement(MenuItem, { disabled: hasMissingPermissions, onClick: toggleEntityShareModal }, /* @__PURE__ */ React.createElement(Icon, { name: "person_add", size: "xs" }), " ", t("step_row_actions.share"))), moreActions, isMutable && /* @__PURE__ */ React.createElement(IfPermitted, { permissions: [`event_procedure_steps:delete:${eventProcedureStep.id}`] }, /* @__PURE__ */ React.createElement(DeleteMenuItem, { onClick: () => handleDelete() }, /* @__PURE__ */ React.createElement(Icon, { name: "delete", size: "xs" }), " ", t("step_row_actions.delete")))), /* @__PURE__ */ React.createElement(
    BootstrapModalConfirm,
    {
      title: t("step_row_actions.delete_title"),
      onConfirm: onDelete,
      onCancel: () => setShowDeleteModal(false),
      showModal: showDeleteModal
    },
    /* @__PURE__ */ React.createElement("div", null, t("step_row_actions.delete_description", { title: eventProcedureStep.title }))
  ), /* @__PURE__ */ React.createElement(
    ConfirmDialog,
    {
      show: showDeleteWarningModal,
      title: t("step_row_actions.warning_title"),
      onConfirm: () => setShowDeleteWarningModal(false),
      hideCancelButton: true,
      btnConfirmText: t("step_row_actions.okay")
    },
    /* @__PURE__ */ React.createElement("div", null, t("step_row_actions.warning_description")),
    /* @__PURE__ */ React.createElement(StyledCol, null, linkedEventProcedures.map((procedure) => /* @__PURE__ */ React.createElement("strong", { key: `procedure-item-${procedure}` }, "\u2022 ", procedure))),
    /* @__PURE__ */ React.createElement("div", null, t("step_row_actions.warning_hint"))
  ), showEntityShareModal && /* @__PURE__ */ React.createElement(
    EntityShareModal,
    {
      entityId: eventProcedureStep.id,
      entityType: "event_procedure_step",
      entityTypeTitle: t("step_row_actions.entity_type_title"),
      entityTitle: eventProcedureStep.title,
      showShareableEntityURL: false,
      description: t("step_row_actions.share_description"),
      onClose: toggleEntityShareModal
    }
  ), pluggableActionModals);
}
export default RowActionMenu;
